import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BlogService } from 'src/app/blog.service';
import { MetaSetterService } from 'src/app/general/meta-setter.service';
import { LocalisationService } from 'src/app/localisation.service';
import { SPORTS } from 'src/app/sports';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-all-articles-page',
	templateUrl: './all-articles-page.component.html',
	styleUrls: ['./all-articles-page.component.scss']
})
export class AllArticlesPageComponent implements OnInit, AfterViewInit {
	selectedCategory = "";
	selectedAuthor: string = null;
	searchQuery = "";
	savedQuery = "";
	blogLoading = true;
	blogError = false;
	title = "";
	icon = "";
	trustedCopy: SafeHtml;
	trustedDesc: SafeHtml;
	savedCategory = "";

	pageNumber = 0;

	nextPageURL: string = "";

	browserMode = false;
	
	environment = environment;

	constructor(
		private metaSetter: MetaSetterService,
		private route: ActivatedRoute,
		private sanitiser: DomSanitizer,
		private blogService: BlogService,
		private localisationService: LocalisationService,
		@Inject(PLATFORM_ID) platformId: object,
	) {
		this.browserMode = isPlatformBrowser(platformId);

		if (this.route.snapshot.queryParamMap.has("sport")) {
		  this.selectedCategory = this.route.snapshot.queryParamMap.get("sport");
		  this.savedCategory = this.selectedCategory;
		}
		if (this.route.snapshot.queryParamMap.has("page") && !isNaN(parseInt(this.route.snapshot.queryParamMap.get("page"), 10))) {
			this.pageNumber = parseInt(this.route.snapshot.queryParamMap.get("page"), 10) - 1;
		} else {
			this.pageNumber = 0;
		}
	}

	ngOnInit(): void {
		this.retrieveAndSetMetadata();
	}

	ngAfterViewInit(): void {
		this.route.queryParams.subscribe((params) => {
			if (params.sport && params.sport.toUpperCase() !== this.savedCategory.toUpperCase()) {
				this.selectedCategory = params.sport;
				this.savedCategory = this.selectedCategory

				this.retrieveAndSetMetadata();
			}
			// handle clearing of sport parameter
			else if (!params.sport && this.savedCategory) {
				this.selectedCategory = "";
				this.savedCategory = this.selectedCategory;
			}
			
			if (params.page && !isNaN(parseInt(params.page, 10)) && parseInt(params.page, 10) - 1 !== this.pageNumber) {
				this.pageNumber = parseInt(params.page, 10) - 1;
				this.retrieveAndSetMetadata();
			}

			if (!params.page || isNaN(parseInt(params.page, 10))) {
				this.pageNumber = 0;
				this.retrieveAndSetMetadata();
			}
		})
	}

	isValidActiveSport(category: string): boolean {
		return this.localisationService.sportPrioritySort(SPORTS).some(s => s.code === category.toUpperCase() && s.active)
	}

	retrieveAndSetMetadata(): void {
		// generate query string from current page's attributes, leaving out any that are on default values
		const canonicalQueryParams: Record<string, string> = {
			sport: this.selectedCategory?.toLowerCase() || undefined,
			page: this.pageNumber === 0 ? undefined : (this.pageNumber + 1).toString()
		};
		// remove undefined fields
		Object.keys(canonicalQueryParams).forEach(key => canonicalQueryParams[key] === undefined ? delete canonicalQueryParams[key] : {});
		let canonicalQueryString = new URLSearchParams(canonicalQueryParams).toString();
		// if query string is not empty, preface it with a question mark
		if (canonicalQueryString) canonicalQueryString = "?" + canonicalQueryString;

		// generate query string from previous page's attributes, leaving out any that are on default values
		const previousQueryParams: Record<string, string> = {
			sport: this.selectedCategory?.toLowerCase() || undefined,
			page: (this.pageNumber - 1 === 0) ? undefined : (this.pageNumber).toString()
		};
		// remove undefined fields
		Object.keys(previousQueryParams).forEach(key => previousQueryParams[key] === undefined ? delete previousQueryParams[key] : {});
		let previousQueryString = new URLSearchParams(previousQueryParams).toString();
		// if query string is not empty, preface it with a question mark
		if (previousQueryString) previousQueryString = "?" + previousQueryString;

		// generate query string from next page's attributes, leaving out any that are on default values
		const nextQueryParams: Record<string, string> = {
			sport: this.selectedCategory?.toLowerCase() || undefined,
			page: (this.pageNumber + 1 === 0) ? undefined : (this.pageNumber + 2).toString()
		};
		// remove undefined fields
		Object.keys(nextQueryParams).forEach(key => nextQueryParams[key] === undefined ? delete nextQueryParams[key] : {});
		let nextQueryString = new URLSearchParams(nextQueryParams).toString();
		// if query string is not empty, preface it with a question mark
		if (nextQueryString) nextQueryString = "?" + nextQueryString;

		this.nextPageURL = `${environment.canonicalDomain}/news${nextQueryString}`

		this.blogService.getNewsMeta(this.selectedCategory || "All")
		.subscribe(settings => {
			this.title = settings.page_title || settings.full_name;
			this.icon = settings.logo?.url;
			this.metaSetter.setMeta({
				title: settings.seo_title,
				seoTitle: settings.seo_title,
				description: settings.seo_description,
				metaImageURL: settings.thumbnail?.url || environment.defaultMetaImage,
				canonicalURL: `${environment.canonicalDomain}/news${canonicalQueryString}`,
				previousURL: this.pageNumber === 0 ? undefined : `${environment.canonicalDomain}/news${previousQueryString}`,
				// nextURL: `${environment.canonicalDomain}/news${nextQueryString}`
			});

			this.trustedCopy = settings.subheading_description ? this.sanitiser.bypassSecurityTrustHtml(settings.subheading_description) : null;
			this.trustedDesc = settings.app_page_subheader ? this.sanitiser.bypassSecurityTrustHtml(settings.app_page_subheader) : null;
		}, () => {
			this.title = "All Articles";
			this.icon = "";
			this.metaSetter.setMeta({
				title: environment.siteTitle,
				description: environment.defaultMetaDescription,
				metaImageURL: environment.defaultMetaImage,
				canonicalURL: `${environment.canonicalDomain}/news${canonicalQueryString}`,
				previousURL: this.pageNumber === 0 ? undefined : `${environment.canonicalDomain}/news${previousQueryString}`,
				// nextURL: `${environment.canonicalDomain}/news${nextQueryString}`
			})
		});
	}

}
